// The console shell: auth gate, routing, chrome.
//
// Routing is the browser's URL, not a `useState` string — which is the one place
// this app deliberately does NOT copy apps/fe. That app switches surfaces with
// `useState` + a `switch`, so nothing is linkable and the back button does
// nothing. Omar needs to open a customer from a link and press back, so the
// console uses real paths. Two of them:
//
//   /                → the customer list
//   /customers/:id   → one customer
//
// ⚠️ Real paths need the host to serve index.html for unknown paths. Cloudflare
// Pages does that by default — the same SPA fallback that made a shared
// stylesheet dangerous (see scripts/sync-design-system.mjs) is exactly what
// makes this work. Worth knowing that one behaviour is both.

import { useCallback, useEffect, useMemo, useState } from "react";
import { ConsoleApi, SIGN_IN_SERVICE_DID_NOT_ANSWER, type StaffIdentity } from "./api.js";
import { type AuthCallbackNotice, login, logout, type SessionClient } from "./auth.js";
import {
  LeftRail,
  RailScrim,
  SURFACE_PATHS,
  type SurfaceId,
  TopHeader,
  useRailDrawer,
} from "./chrome.js";
import { CourseImportScreen } from "./course-import.js";
import { ImageProfilesScreen } from "./image-profiles.js";
import { RequestsScreen } from "./requests.js";
import { CustomerDetailScreen, CustomersScreen, NotBuiltYet } from "./screens.js";

type Route = { name: "customers" } | { name: "customer"; id: string } | { name: SurfaceId };

/** The start screen when our own server was not reached: then the operator's connection IS a
 *  suspect. When our server answered and Auth0 did not, it says SIGN_IN_SERVICE_DID_NOT_ANSWER. */
const SERVICE_NOT_REACHED = "Can’t reach the sign-in service. Check your connection, then try again.";

/** Exported for `image-profiles.test.tsx`, which asserts that every rail
 *  destination in `SURFACE_PATHS` parses back to the surface it came from. That
 *  round trip is the thing that silently broke when the two were separate string
 *  expressions, and it cannot be checked from outside the module. */
export function parseRoute(path: string): Route {
  const m = /^\/customers\/([^/]+)\/?$/.exec(path);
  if (m?.[1]) return { name: "customer", id: decodeURIComponent(m[1]) };
  // Read from the SAME map the rail navigates by, so a surface can never have a
  // link that leads nowhere. "/" is skipped: it is the fallback below, and
  // `startsWith("/")` would match everything.
  for (const [id, prefix] of Object.entries(SURFACE_PATHS) as [SurfaceId, string][]) {
    if (prefix !== "/" && path.startsWith(prefix)) return { name: id };
  }
  return { name: "customers" };
}

function useRoute() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const go = useCallback((next: string) => {
    window.history.pushState({}, "", next);
    setPath(next);
  }, []);
  return { route: parseRoute(path), go };
}

export function App({
  session,
  apiBase,
  notice = null,
}: {
  /** The console's half of the sign-in service (plan 27). See auth.ts. */
  session: SessionClient;
  apiBase: string;
  /** A sign-in that came back without finishing, without stopping the app. See auth.ts. */
  notice?: AuthCallbackNotice | null;
}) {
  const { route, go } = useRoute();
  const rail = useRailDrawer();
  const api = useMemo(() => new ConsoleApi(apiBase, session), [apiBase, session]);

  const [auth, setAuth] = useState<
    | { t: "checking" }
    | { t: "anonymous" }
    | { t: "signed-in"; me: StaffIdentity }
    | { t: "not-staff"; message: string }
    | { t: "unavailable"; message: string }
  >({ t: "checking" });

  // Where the person was going before signing in needs no step here: our server
  // brings the full-page sign-in back to that path itself (auth.ts).

  useEffect(() => {
    let live = true;
    (async () => {
      let signedIn = false;
      try {
        signedIn = (await session.status()).signedIn;
      } catch {
        // Our sign-in service did not answer: that is not "signed out", and the
        // Sign in button would not help. Say what is true — and here the operator's
        // own connection IS a suspect, because our server itself was not reached.
        if (live) setAuth({ t: "unavailable", message: SERVICE_NOT_REACHED });
        return;
      }
      if (!signedIn) {
        if (live) setAuth({ t: "anonymous" });
        return;
      }
      try {
        const me = await api.me();
        if (live) setAuth({ t: "signed-in", me });
      } catch (err) {
        // Our sign-in service did not answer while fetching the pass (Auth0 slow or
        // rate-limiting): the same honest screen as above, with Try again — not
        // "No access", which would send the operator to the Auth0 dashboard (QA of plan 27).
        // Our server answered the status check just above, so it is Auth0 that did not:
        // no "check your connection" (Omar, 2026-10-18) — the same sentence the screens'
        // error box gives this condition (api.ts).
        if ((err as { code?: string }).code === "auth_unavailable") {
          if (live) setAuth({ t: "unavailable", message: SIGN_IN_SERVICE_DID_NOT_ANSWER });
          return;
        }
        // ★ A 403 here is the SPECIFIC, LIKELY failure and it must not read as a
        // generic error: it means the sign-in worked and the Auth0 `super_admin`
        // role is missing from this user. Next door, an admin console was built,
        // fully tested and unopenable by any human for two months because every
        // test minted its own token containing the role. Naming the cause is the
        // difference between a dashboard fix and a hunt through the deployment.
        const status = (err as { status?: number }).status;
        if (live && status === 403) {
          setAuth({
            t: "not-staff",
            message:
              "You signed in, but this account does not carry the `super_admin` role, " +
              "so the console will not open. Add the role to your user in the Auth0 " +
              "dashboard (User Management → Users → Roles), then sign out and in again — " +
              "the role is read from the token, so an existing session will not pick it up.",
          });
        } else if (live) {
          setAuth({
            t: "not-staff",
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [api, session]);

  if (auth.t === "checking") {
    return <Centered>Checking your access…</Centered>;
  }

  if (auth.t === "anonymous") {
    return (
      <Centered>
        {/*
          ★ The notice goes ABOVE the button, and the button is always here.
          This screen is what a failed sign-in return leg now lands on — a stale
          link, a mismatched state, a refusal from Auth0, or an unrecognised
          fault. Previously none of them reached it: the failure was thrown at
          the boot handler, which rendered an error page INSTEAD of the app, so
          the one control that fixes the problem was removed at exactly the
          moment it was needed (`feedback_an_error_screen_that_replaces_the_app_removes_the_remedy`).

          A `fault` is shown as an error and carries its technical text, because
          "sign-in is broken" and "your link went stale" call for different next
          moves and must not look alike. Everything else is a calm line: the
          person did nothing wrong and signing in again is the whole fix.
        */}
        {notice && (
          <div style={{ marginBottom: 16, textAlign: "left" }}>
            <div
              className={notice.recoverable ? "pill" : "pill error"}
              style={{ marginBottom: 8 }}
            >
              {notice.recoverable ? "Sign-in did not complete" : "Sign-in failed"}
            </div>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "var(--text)" }}>
              {notice.message}
            </p>
            {notice.detail && !notice.recoverable && (
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: "var(--text-muted)",
                }}
              >
                {notice.detail}
              </p>
            )}
          </div>
        )}
        <p style={{ margin: "0 0 14px", fontSize: 13, color: "var(--text-muted)" }}>
          Dynamo staff sign-in.
        </p>
        <button className="btn primary lg focusable" onClick={() => login()}>
          Sign in
        </button>
      </Centered>
    );
  }

  if (auth.t === "unavailable") {
    return (
      <Centered>
        <div className="pill error" style={{ marginBottom: 12 }}>
          Sign-in unavailable
        </div>
        <p style={{ margin: "0 0 14px", fontSize: 13, lineHeight: 1.6, color: "var(--text)" }}>
          {auth.message}
        </p>
        <button className="btn primary focusable" onClick={() => window.location.reload()}>
          Try again
        </button>
      </Centered>
    );
  }

  if (auth.t === "not-staff") {
    return (
      <Centered>
        <div className="pill error" style={{ marginBottom: 12 }}>
          No access
        </div>
        <p
          style={{
            margin: "0 0 14px",
            fontSize: 13,
            lineHeight: 1.6,
            color: "var(--text)",
            textAlign: "left",
          }}
        >
          {auth.message}
        </p>
        <button className="btn focusable" onClick={() => void logout(session)}>
          Sign out
        </button>
      </Centered>
    );
  }

  const current: SurfaceId = route.name === "customer" ? "customers" : route.name;

  return (
    <div className="console-shell">
      <TopHeader
        email={auth.me.email}
        onSignOut={() => void logout(session)}
        onToggleRail={rail.toggle}
      />
      <LeftRail
        current={current}
        open={rail.open}
        onClose={rail.close}
        onNavigate={(id) => go(SURFACE_PATHS[id])}
      />
      {rail.open && <RailScrim onClose={rail.close} />}
      <main className="console-main">
        {route.name === "customers" && (
          <CustomersScreen api={api} onOpen={(id) => go(`/customers/${encodeURIComponent(id)}`)} />
        )}
        {route.name === "customer" && (
          <CustomerDetailScreen api={api} id={route.id} onBack={() => go("/")} />
        )}
        {route.name === "requests" && <RequestsScreen api={api} />}
        {route.name === "imageProfiles" && <ImageProfilesScreen api={api} />}
        {/* ⚠️ THIS LINE HAS NO COMPILE-TIME GUARD. `SURFACE_PATHS` is total over
            `SurfaceId`, so a new surface cannot lack a URL — but nothing obliges
            it to be RENDERED. Omitting this leaves the rail entry working, the
            route parsing, tsc silent and the page blank
            (`feedback_gated_walked_editable_must_agree`). `course-import.test.tsx`
            renders this shell at `/course-import` and fails without it. */}
        {route.name === "courseImport" && <CourseImportScreen api={api} />}
        {route.name === "activity" && <NotBuiltYet surface={route.name} />}
      </main>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg)",
        padding: 24,
      }}
    >
      <div
        className="card"
        style={{ padding: 28, maxWidth: 460, textAlign: "center", width: "100%" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginBottom: 16,
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 14, color: "var(--text-strong)" }}>Dynamo</span>
          <span
            style={{
              fontSize: 11,
              color: "var(--text-faint)",
              padding: "1px 5px",
              background: "var(--surface-inset)",
              borderRadius: 3,
              fontWeight: 500,
            }}
          >
            Console
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}
